import React, { FC, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { List as ImmutableList } from 'immutable';
import { useIntl } from 'react-intl';
import { Typography } from '@material-ui/core';
import { CampaignType, CampaignStatus as ECampaignStatus } from 'common/constants/campaign';
import { PeakModules } from 'common/constants/peakModules';
import { ICampaignListItem, ICampaignListItemImt } from 'modules/crm/interfaces/campaigns';
import { IBodyCell, IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';
import { IFilterSettings } from 'common/interfaces/filter';
import { ActionsMenu, BooleanLabel, Table } from 'common/components';
import AddCampaignDropdown from './AddCampaignDropdown/AddCampaignDropdown';
import SmallScreenCell from './SmallScreenCell/SmallScreenCell';
import CampaignStatus from './CampaignStatus/CampaignStatus';
import tableHeaders from 'common/messages/tableHeaders';
import commonMessages from 'common/messages/messages';
import { CampaignTypes } from '../../constants/campaigns';
import common from '../../messages/common';
import ptCrmMessages from 'modules/personal-training-crm/messages/messages';
import { HEADER_ACTIONS_OPTION } from 'constants/table.constants';
import { useChangeCRMCampaignStatus } from 'modules/crm/api/campaign.api';
import { useChangePTCRMCampaignStatus } from 'modules/personal-training-crm/api/campaign.api';
import { fetchCampaigns } from 'modules/crm/state/campaigns/actions';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import ChangeActiveStatusModal from 'common/modals/ChangeActiveStatusModal/ChangeActiveStatusModal';
import ActionItemEdit from 'components/ActionItems/ActionItemEdit';
import ActionItemChangeStatus from 'components/ActionItems/ActionItemChangeStatus';

interface ICampaignsTable {
  items: ImmutableList<ICampaignListItemImt>;
  tableParams: ITableParams;
  tableFilterSettings: IFilterSettings[];
  totalRows: number;
  isLoading: boolean;
  onChangeParams: (tableParams: ITableParams) => void;
  module: PeakModules.Crm | PeakModules.PersonalTrainingCrm;
}

const CampaignsTable: FC<ICampaignsTable> = ({
  isLoading,
  items,
  totalRows,
  tableParams,
  tableFilterSettings,
  onChangeParams,
  module,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [changeCRMCampaignStatus, crmCampaignReqData] = useChangeCRMCampaignStatus();
  const [changePTCRMCampaignStatus, ptCrmCampaignReqData] = useChangePTCRMCampaignStatus();
  const [changeStatusCampaign, setChangeStatusCampaign] = useState<ICampaignListItem | null>(null);

  const isCrmModule = module === PeakModules.Crm;
  const isPTCrmModule = module === PeakModules.PersonalTrainingCrm;
  let hasCreatePermission = false;
  let hasEditPermission = false;
  let hasChangeStatusPermission = false;

  const {
    CRM_CAMPAIGN_CREATE,
    CRM_CAMPAIGN_EDIT,
    CRM_CAMPAIGN_CHANGE_STATUS,
    PT_CRM_CAMPAIGN_CREATE,
    PT_CRM_CAMPAIGN_EDIT,
    PT_CRM_CAMPAIGN_CHANGE_STATUS,
  } = usePermissionContext();

  if (isCrmModule) {
    hasCreatePermission = CRM_CAMPAIGN_CREATE;
    hasEditPermission = CRM_CAMPAIGN_EDIT;
    hasChangeStatusPermission = CRM_CAMPAIGN_CHANGE_STATUS;
  } else if (isPTCrmModule) {
    hasCreatePermission = PT_CRM_CAMPAIGN_CREATE;
    hasEditPermission = PT_CRM_CAMPAIGN_EDIT;
    hasChangeStatusPermission = PT_CRM_CAMPAIGN_CHANGE_STATUS;
  }

  const headerOptions: IHeadCell[] = [
    { id: 'name', label: intl.formatMessage(tableHeaders.name), sort: true },
    { id: 'type', label: intl.formatMessage(tableHeaders.type), sort: true },
    { id: 'script', label: intl.formatMessage(tableHeaders.script), sort: true },
    { id: 'status', label: intl.formatMessage(tableHeaders.status), sort: true },
    { id: 'automatic', label: intl.formatMessage(commonMessages.automaticLabel), sort: true },
  ];

  if (hasEditPermission || hasChangeStatusPermission) {
    headerOptions.push(HEADER_ACTIONS_OPTION);
  }

  const handleEditAction = useCallback(
    (id: string, type: CampaignType) => {
      navigate(`/${location.pathname.split('/')[1]}/campaigns/${id}/edit?type=${type}`);
    },
    [location.pathname, navigate],
  );

  const tableRows: ITableRow[] = items
    .map(
      (campaign): ITableRow => {
        let btnEdit: JSX.Element | null = null;
        let btnChangeStatus: JSX.Element | null = null;

        const bodyCells: IBodyCell[] = [
          {
            label: campaign.get('title'),
            className: 'body-cell',
            variant: 'subtitle2',
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: (
              <Typography>{CampaignTypes.translate(campaign.get('campaignType'))}</Typography>
            ),
          },
          {
            label: campaign.getIn(['script', 'title']),
            className: 'body-cell',
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: <CampaignStatus status={campaign.get('status')} />,
          },
          {
            label: '',
            className: 'body-cell',
            cellComponent: <BooleanLabel value={campaign.get('automatic')} />,
          },
        ];

        const bodyCellsMini: IBodyCell[] = [
          {
            label: '',
            className: 'body-cell',
            cellComponent: <SmallScreenCell campaign={campaign.toJS()} />,
          },
        ];

        if (hasEditPermission) {
          btnEdit = (
            <ActionItemEdit
              onClick={() => handleEditAction(campaign.get('id'), campaign.get('campaignType'))}
            />
          );
        }

        if (hasChangeStatusPermission) {
          btnChangeStatus = (
            <ActionItemChangeStatus
              active={campaign.get('status') === ECampaignStatus.Active}
              onClick={() => setChangeStatusCampaign(campaign.toJS())}
            />
          );
        }

        if (btnEdit || btnChangeStatus) {
          const actionsMenu = (
            <ActionsMenu horizontal tableActionsMode>
              {btnEdit}
              {btnChangeStatus}
            </ActionsMenu>
          );

          bodyCells.push({
            label: 'actions',
            className: 'body-actions-cell',
            cellComponent: actionsMenu,
          });

          bodyCellsMini.push({
            label: 'actions',
            align: 'center',
            padding: 'none',
            cellComponent: actionsMenu,
          });
        }

        return {
          id: campaign.get('id'),
          cells: bodyCells,
          cellsMini: bodyCellsMini,
        };
      },
    )
    .toJS();

  return (
    <>
      <Table
        title={intl.formatMessage(isCrmModule ? common.campaigns : ptCrmMessages.ptCampaigns)}
        addButton={hasCreatePermission ? <AddCampaignDropdown /> : null}
        headerOptions={headerOptions}
        showPerPageSelect
        rows={tableRows}
        filters={tableFilterSettings}
        totalRows={totalRows}
        isLoading={isLoading}
        onChangeParams={onChangeParams}
        tableParams={tableParams}
      />

      {changeStatusCampaign ? (
        <ChangeActiveStatusModal
          isLoading={crmCampaignReqData.isLoading || ptCrmCampaignReqData.isLoading}
          isActive={changeStatusCampaign.status === ECampaignStatus.Active}
          title={changeStatusCampaign.title}
          onClose={() => setChangeStatusCampaign(null)}
          onSubmit={() => {
            const active = changeStatusCampaign.status === ECampaignStatus.Active;
            const id = changeStatusCampaign.id;

            if (isCrmModule) {
              changeCRMCampaignStatus({ id, active: !active })
                .then(() => {
                  dispatch(fetchCampaigns(tableParams, module));
                })
                .catch(() => {})
                .finally(() => {
                  setChangeStatusCampaign(null);
                });
            } else if (isPTCrmModule) {
              changePTCRMCampaignStatus({ id, active: !active })
                .then(() => {
                  dispatch(fetchCampaigns(tableParams, module));
                })
                .catch(() => {})
                .finally(() => {
                  setChangeStatusCampaign(null);
                });
            }
          }}
        />
      ) : null}
    </>
  );
};

export default CampaignsTable;
